import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Search, PenLine, Lock, X, Wand2 } from "lucide-react";
import { toast } from "sonner";
import AddressSearch from "@/components/AddressSearch";
import { authFetch, createManualParcel, followParcel } from "@/lib/api";
import CulturePicker from "@/components/CulturePicker";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const TABS = [{ v: "adresse", l: "Adresse" }, { v: "cadastre", l: "Cadastre" }, { v: "numero", l: "N° de parcelle" }];
const fmtHa = (v) => Number(v || 0).toLocaleString("fr-FR", { maximumFractionDigits: 2 });
export const cadLabel = (r) => `${String(r.section).replace(/^0/, "")} ${parseInt(r.numero, 10)}`;

/** Contour cadastral (GeoJSON) → points du tracé manuel (anneau extérieur du plus grand polygone). */
export function cadastreToPoints(geom) {
  const polys = geom?.type === "MultiPolygon" ? geom.coordinates : geom?.type === "Polygon" ? [geom.coordinates] : [];
  const ring = polys.map((p) => p?.[0] || []).sort((a, b) => b.length - a.length)[0];
  if (!ring?.length) return [];
  const closed = ring.length > 1 && ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1];
  return (closed ? ring.slice(0, -1) : ring).map(([lon, lat]) => ({ lat: Number(lat.toFixed(6)), lon: Number(lon.toFixed(6)) }));
}
const norm = (t) => (t || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/\s+/g, "");

/**
 * Recherche de la carte : adresse, référence cadastrale (La Réunion) ou numéro de parcelle.
 * Le cadastre affiche le contour officiel et les parcelles agricoles qui s'y trouvent ; sans
 * parcelle agricole, il propose de créer la parcelle à partir du contour cadastral.
 */
export default function MapSearch({ parcels = [], onAddressSelect, onSelectParcel, onCadastreResults, onCreateFromCadastre, onParcelConverted }) {
  const [tab, setTab] = useState("adresse");
  return (
    <div className="space-y-2" data-testid="map-search">
      <div className="flex gap-1 rounded-lg p-0.5" style={{ background: "#F1EEE7" }} role="tablist">
        {TABS.map((t) => (
          <button key={t.v} role="tab" aria-selected={tab === t.v} onClick={() => setTab(t.v)} data-testid={`map-search-tab-${t.v}`}
            className={`flex-1 h-7 rounded-md text-[11.5px] font-semibold transition ${tab === t.v ? "bg-white shadow-sm" : "text-gray-500"}`}
            style={tab === t.v ? { color: "#2D6A4F" } : undefined}>
            {t.l}
          </button>
        ))}
      </div>
      {tab === "adresse" && <AddressSearch onSelect={onAddressSelect} />}
      {tab === "cadastre" && <CadastreSearch onSelectParcel={onSelectParcel} onResults={onCadastreResults} onCreate={onCreateFromCadastre} onConverted={onParcelConverted} parcels={parcels} />}
      {tab === "numero" && <NumberSearch parcels={parcels} onSelectParcel={onSelectParcel} />}
    </div>
  );
}

function CadastreSearch({ parcels, onSelectParcel, onResults, onCreate, onConverted }) {
  const [communes, setCommunes] = useState([]);
  const [insee, setInsee] = useState("");
  const [section, setSection] = useState("");
  const [numero, setNumero] = useState("");
  const [full, setFull] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const resultsRef = useRef(null);

  // Les résultats (et le bouton de conversion) sont sous le formulaire : on les amène à l'écran
  useEffect(() => {
    if (data?.results?.length) setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
  }, [data]);

  useEffect(() => {
    authFetch(`${API}/cadastre/communes`).then((r) => (r.ok ? r.json() : [])).then(setCommunes).catch(() => {});
  }, []);

  const search = async (e) => {
    e?.preventDefault();
    // Section seule (sans numéro) : toutes nos parcelles rattachées à cette section
    const q = full.trim() || `${section.trim()} ${numero.trim()}`.trim();
    if (!q || (!full.trim() && (!insee || !section.trim()))) {
      setError("Choisissez la commune et indiquez la section (et le numéro), ou collez la référence complète.");
      return;
    }
    setBusy(true); setError("");
    try {
      const res = await authFetch(`${API}/cadastre/search?q=${encodeURIComponent(q)}${insee ? `&code_insee=${insee}` : ""}`);
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Recherche impossible");
      setData(d);
      // Seuls les résultats avec contour se dessinent sur la carte (section, ou IGN indisponible : pas de contour)
      onResults?.((d.results || []).filter((r) => r.geometry));
      if (!(d.results || []).length) setError((d.not_found || [])[0] || "Aucune parcelle cadastrale trouvée.");
    } catch (err) { setError(err.message); setData(null); onResults?.([]); }
    setBusy(false);
  };

  const clear = () => { setData(null); setError(""); onResults?.([]); };
  const openParcel = (id) => {
    const p = parcels.find((x) => x.parcel_id === id);
    if (p) { onSelectParcel?.(p); return; }
    authFetch(`${API}/parcels/${encodeURIComponent(id)}`).then((r) => (r.ok ? r.json() : null)).then((d) => d && onSelectParcel?.(d)).catch(() => {});
  };

  return (
    <div className="space-y-2" data-testid="cadastre-search">
      <form onSubmit={search} className="space-y-2">
        <select value={insee} onChange={(e) => setInsee(e.target.value)} className="w-full h-8 rounded-md border border-black/10 bg-white px-2 text-xs" data-testid="cadastre-commune" aria-label="Commune">
          <option value="">Commune…</option>
          {communes.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
        </select>
        <div className="grid grid-cols-2 gap-2">
          <input value={section} onChange={(e) => setSection(e.target.value.toUpperCase())} maxLength={2} placeholder="Section (ex. AS)"
            className="h-8 rounded-md border border-black/10 px-2 text-xs font-mono" data-testid="cadastre-section" aria-label="Section" />
          <input value={numero} onChange={(e) => setNumero(e.target.value.replace(/[^0-9]/g, ""))} maxLength={4} inputMode="numeric" placeholder="Numéro (ex. 1324)"
            className="h-8 rounded-md border border-black/10 px-2 text-xs font-mono" data-testid="cadastre-numero" aria-label="Numéro" />
        </div>
        <input value={full} onChange={(e) => setFull(e.target.value)} placeholder="ou référence complète : 97409000AS1324"
          className="w-full h-8 rounded-md border border-black/10 px-2 text-xs font-mono" data-testid="cadastre-full" aria-label="Référence complète" />
        <button type="submit" disabled={busy} className="w-full h-8 rounded-md text-white text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-60"
          style={{ background: "#2D6A4F" }} data-testid="cadastre-submit">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />} Rechercher au cadastre
        </button>
        <p className="text-[10px] text-gray-500">Plusieurs parcelles : séparez-les par des virgules (« AS 1324, AS 246 »). Sans numéro : toutes les parcelles de la carte rattachées à la section. La Réunion uniquement.</p>
      </form>

      {error && <p className="text-[11px] rounded-md px-2 py-1.5" style={{ background: "#FCF1ED", color: "#9A3B1F" }} data-testid="cadastre-error">{error}</p>}

      {data && (data.results || []).length > 0 && (
        <div ref={resultsRef} className="space-y-2 scroll-mt-16" data-testid="cadastre-results">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[10.5px] text-gray-600" data-testid="cadastre-results-summary">
              {data.mode === "section"
                ? `Section ${String(data.results[0].section).replace(/^0/, "")} : ${data.results.length} parcelle${data.results.length > 1 ? "s" : ""} cadastrale${data.results.length > 1 ? "s" : ""} rattachée${data.results.length > 1 ? "s" : ""} à la carte`
                : ""}
            </p>
            <button onClick={clear} className="shrink-0 text-[10px] text-gray-500 flex items-center gap-0.5"><X className="w-3 h-3" /> Effacer</button>
          </div>
          {data.results.map((r) => (
            <div key={r.idu} className="rounded-lg border p-2 space-y-1.5" style={{ borderColor: "#E07A5F55", background: "#E07A5F08" }} data-testid={`cadastre-result-${r.idu}`}>
              <p className="text-[11.5px] font-bold text-gray-900">
                Parcelle cadastrale {cadLabel(r)}
                <span className="font-normal text-gray-500"> · {r.commune}{r.surface_ha != null ? ` · ${fmtHa(r.surface_ha)} ha` : ""}</span>
              </p>
              {r.parcels.map((p) => (
                <button key={p.parcel_id} onClick={() => openParcel(p.parcel_id)} data-testid={`cadastre-parcel-${p.parcel_id}`}
                  className="w-full text-left rounded-md px-2 py-1.5 bg-white border border-black/5 hover:border-emerald-600">
                  <span className="text-[11.5px] font-semibold text-gray-900 flex items-center gap-1">{p.name || p.parcel_id}{p.locked && <Lock className="w-3 h-3 text-amber-700" />}</span>
                  <span className="block text-[10px] text-gray-500">
                    {fmtHa(p.surface_ha)} ha · {p.share_of_parcel_pct == null ? "sur" : p.share_of_parcel_pct >= 95 ? "entièrement dans" : `${p.share_of_parcel_pct} % dans`} cette parcelle cadastrale · Ouvrir la fiche
                  </span>
                </button>
              ))}
              {!r.parcels.length && <p className="text-[10.5px] text-gray-600">Aucune parcelle agricole enregistrée sur cette parcelle cadastrale.</p>}
              {r.geometry && <ConvertCadastre r={r} onCreate={onCreate}
                onDone={(created) => {
                  setData((d) => ({ ...d, results: d.results.map((x) => (x.idu === r.idu ? { ...x, parcels: [...x.parcels, {
                    parcel_id: created.parcel_id, name: created.name, surface_ha: created.surface_ha, manual: true, locked: false,
                    share_of_parcel_pct: 100, share_of_cadastre_pct: 100 }] } : x)) }));
                  onConverted?.(created);
                }} />}
            </div>
          ))}
          {(data.not_found || []).map((m) => <p key={m} className="text-[10.5px] text-amber-800">{m}</p>)}
        </div>
      )}
    </div>
  );
}

/** « Convertir en parcelle saisie » : le contour cadastral devient une parcelle de l'appli (saisie manuelle). */
function ConvertCadastre({ r, onCreate, onDone }) {
  const [open, setOpen] = useState(false);
  const [culture, setCulture] = useState("");
  const [saving, setSaving] = useState(false);
  const formRef = useRef(null);
  const covered = Math.max(0, ...r.parcels.map((p) => p.share_of_cadastre_pct || 0));

  // Le formulaire s'ouvre sous le bouton : on l'amène entièrement à l'écran (bouton « Enregistrer » compris)
  useEffect(() => {
    if (open) setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }), 100);
  }, [open]);

  const save = async () => {
    if (!culture.trim()) { toast.error("Choisissez la culture de la parcelle"); return; }
    const points = cadastreToPoints(r.geometry);
    if (points.length < 3) { toast.error("Contour cadastral illisible : utilisez « Ajuster le tracé »"); return; }
    setSaving(true);
    try {
      const res = await createManualParcel({
        name: `Parcelle ${cadLabel(r)}`, commune: r.commune, culture_type: culture.trim(), points,
        notes: `Créée depuis le cadastre : ${cadLabel(r)} (${r.idu}), ${fmtHa(r.surface_ha)} ha`,
      });
      const created = res.data;
      try { await followParcel(created.parcel_id); } catch { /* suivi facultatif */ }
      toast.success(`Parcelle ${cadLabel(r)} enregistrée et ajoutée à vos parcelles suivies`);
      setOpen(false);
      onDone?.(created);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Enregistrement impossible");
    }
    setSaving(false);
  };

  if (!open) {
    return (
      <div className="space-y-1">
        <button onClick={() => setOpen(true)} className="w-full h-8 rounded-md text-xs font-semibold flex items-center justify-center gap-1.5 text-white"
          style={{ background: "#8B5E3C" }} data-testid={`cadastre-convert-${r.idu}`}>
          <Wand2 className="w-3.5 h-3.5" /> Convertir en parcelle saisie
        </button>
        {covered >= 50 && <p className="text-[10px] text-amber-800">Une parcelle de la carte couvre déjà {covered} % de cette parcelle cadastrale : vérifiez qu'il ne s'agit pas de la même.</p>}
      </div>
    );
  }
  return (
    <div ref={formRef} className="rounded-md bg-white border p-2 space-y-1.5 scroll-mb-4" style={{ borderColor: "#8B5E3C55" }} data-testid={`cadastre-convert-form-${r.idu}`}>
      <p className="text-[10.5px] text-gray-600">Le contour cadastral {cadLabel(r)} ({fmtHa(r.surface_ha)} ha) devient une parcelle saisie, suivie dans votre compte.</p>
      <div className="flex gap-1.5 items-start">
        <CulturePicker value={culture} onChange={setCulture} region={(r.idu || "").startsWith("976") ? "mayotte" : "reunion"}
          className="!h-8 text-xs" inputClassName="!h-8 text-xs" testId={`cadastre-convert-culture-${r.idu}`} />
        <button onClick={save} disabled={saving} className="shrink-0 h-8 px-3 rounded-md text-white text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-60"
          style={{ background: "#2D6A4F" }} data-testid={`cadastre-convert-save-${r.idu}`}>
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Enregistrer
        </button>
      </div>
      <div className="flex justify-between">
        {onCreate && <button onClick={() => onCreate(r)} className="text-[10.5px] font-semibold flex items-center gap-1" style={{ color: "#8B5E3C" }} data-testid={`cadastre-create-${r.idu}`}>
          <PenLine className="w-3 h-3" /> Ajuster le tracé avant</button>}
        <button onClick={() => setOpen(false)} className="text-[10.5px] text-gray-500">Annuler</button>
      </div>
    </div>
  );
}

function NumberSearch({ parcels, onSelectParcel }) {
  const [q, setQ] = useState("");
  const hits = useMemo(() => {
    const n = norm(q);
    if (n.length < 3) return [];
    return parcels.filter((p) => norm(p.parcel_id).includes(n) || norm(p.rpg_id).includes(n) || norm(p.name).includes(n)).slice(0, 20);
  }, [q, parcels]);
  return (
    <div className="space-y-2" data-testid="number-search">
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="RE-97409-20260813-0123 ou n° d'îlot PAC"
        className="w-full h-8 rounded-md border border-black/10 px-2 text-xs font-mono" data-testid="number-search-input" aria-label="Numéro de parcelle" />
      {q.trim().length >= 3 && !hits.length && <p className="text-[11px] text-gray-500">Aucune parcelle avec ce numéro sur la carte.</p>}
      {hits.map((p) => (
        <button key={p.parcel_id} onClick={() => onSelectParcel?.(p)} className="w-full text-left rounded-md px-2 py-1.5 bg-white border border-black/5 hover:border-emerald-600"
          data-testid={`number-hit-${p.parcel_id}`}>
          <span className="text-[11.5px] font-semibold text-gray-900">{p.name || p.parcel_id}</span>
          <span className="block text-[10px] text-gray-500 font-mono">{p.parcel_id}{p.rpg_id ? ` · îlot ${p.rpg_id}` : ""} · {fmtHa(p.surface_ha)} ha</span>
        </button>
      ))}
    </div>
  );
}
